import React, { useState } from 'react';
import '../stylings/BucksFooter.css';

function BucksFooter() {
  // Get current year for copyright
  const currentYear = new Date().getFullYear();
  
  // State for email subscription
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState(null);
  const [subscribeError, setSubscribeError] = useState(null);
  
  // Handle newsletter subscription
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    // Reset message states
    setSubscribeMessage(null);
    setSubscribeError(null);
    
    // Validate email
    if (!email || !email.includes('@') || !email.includes('.')) {
      setSubscribeError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-email-subscriber-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubscribeMessage(data.message || 'Thank you for subscribing!');
        setEmail(''); // Clear the email input on success
      } else {
        setSubscribeError(data.error || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting newsletter form:', err);
      setSubscribeError('Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <footer className="bucks-footer">
      <div className="footer-content">
        <div className="footer-top">
          <div className="footer-column">
            <h3>Shop</h3>
            <ul className="footer-links">
              <li><a href="/shop">All Products</a></li>
              <li><a href="/shop?category=fruit">Fruit</a></li>
              <li><a href="/shop?category=vegetables">Vegetables</a></li>
              <li><a href="/shop?category=preserves">Preserves</a></li>
              <li><a href="/shop?category=merch">Merch</a></li>
            </ul>
          </div>
          
          <div className="footer-column">
            <h3>About Us</h3>
            <ul className="footer-links">
              <li><a href="/about">Our Story</a></li>
              <li><a href="/about#farmers">Meet The Farmers</a></li>
              <li><a href="/bucks-box">Buck's Box</a></li>
              <li><a href="/recipes">Seasonal Recipes</a></li>
            </ul>
          </div>
          
          <div className="footer-column">
            <h3>Visit Us</h3>
            <address className="footer-address">
              <p>4250 W 123rd St</p>
              <p>Alsip, IL 60803</p>
              <p className="footer-hours">
                <span>Mon-Fri: 9AM – 4PM</span>
                <span>Sat: 9AM – 1PM</span>
                <span>Sun: Closed</span>
              </p>
            </address>
          </div>
          
          <div className="footer-column newsletter">
            <h3>Stay Connected</h3>
            <p>Sign up for our newsletter to receive seasonal updates and special offers.</p>
            <form onSubmit={handleNewsletterSubmit} className="footer-newsletter-form">
              <div className="newsletter-form">
                <input 
                  type="email" 
                  placeholder="Your email address" 
                  className="newsletter-input"
                  aria-label="Email address for newsletter"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button 
                  type="submit" 
                  className="newsletter-btn" 
                  aria-label="Subscribe to newsletter"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
              
              {/* Message display */}
              {subscribeMessage && (
                <div className="footer-newsletter-success">
                  {subscribeMessage}
                </div>
              )}
              
              {subscribeError && (
                <div className="footer-newsletter-error">
                  {subscribeError}
                </div>
              )}
            </form>
          </div>
        </div>
        
        <div className="footer-divider"></div>
        
        <div className="footer-bottom">
          <div className="social-links">
            <a href="https://facebook.com" aria-label="Buck's Farmstand on Facebook" className="social-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
              </svg>
            </a>
            <a href="https://instagram.com" aria-label="Buck's Farmstand on Instagram" className="social-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
            </a>
            <a href="https://twitter.com" aria-label="Buck's Farmstand on Twitter" className="social-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
              </svg>
            </a>
          </div>
          
          <div className="logo-column">
            <div className="footer-logo">
              <span className="footer-logo-text">Buck's Farmstand</span>
            </div>
            <p className="tagline">Farm Fresh, Since 1975</p>
          </div>
          
          <div className="legal-links">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href="/contact">Contact Us</a>
          </div>
        </div>
        
        <div className="copyright">
          <p>&copy; {currentYear} Buck's Farmstand. All Rights Reserved.</p>
        </div>
      </div>

      {/* Add styles for newsletter form states */}
      <style>
        {`
          .footer-newsletter-form {
            width: 100%;
          }
          
          .footer-newsletter-success {
            margin-top: 0.8rem;
            padding: 0.6rem 0.8rem;
            background-color: rgba(255, 255, 255, 0.9);
            color: #5a8a44;
            border-radius: 4px;
            font-size: 0.9rem;
            font-weight: 600;
            animation: fadeIn 0.5s ease;
          }
          
          .footer-newsletter-error {
            margin-top: 0.8rem;
            padding: 0.6rem 0.8rem;
            background-color: rgba(255, 255, 255, 0.9);
            color: #a85d5d;
            border-radius: 4px;
            font-size: 0.9rem;
            font-weight: 600;
            animation: fadeIn 0.5s ease;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @media (max-width: 768px) {
            .footer-newsletter-success,
            .footer-newsletter-error {
              text-align: center;
            }
          }
        `}
      </style>
    </footer>
  );
}

export default BucksFooter;